import axios from 'axios';

// Create axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // Include credentials in requests
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching issues
    config.params = {
      ...config.params,
      _t: Date.now()
    };

    // Add auth token if available (for future use)
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        data: config.data,
        params: config.params
      });
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
    }

    return response;
  },
  (error) => {
    // Handle different types of errors
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      console.error('❌ API Error Response:', {
        status,
        message: data?.message || 'Unknown error',
        url: error.config?.url,
        data: data
      });

      // Handle specific error codes
      switch (status) {
        case 401:
          // Unauthorized - clear auth token and redirect to login
          localStorage.removeItem('authToken');
          // In a real app, you might want to redirect to login page
          break;
        case 403:
          // Forbidden
          console.error('Access forbidden');
          break;
        case 404:
          // Not found
          console.error('Resource not found');
          break;
        case 422:
          // Validation error
          console.error('Validation error:', data?.errors || data?.details);
          break;
        case 429:
          // Rate limit exceeded
          console.error('Too many requests, please try again later');
          break;
        case 500:
          // Internal server error
          console.error('Internal server error');
          break;
        default:
          console.error(`HTTP Error ${status}: ${data?.message || 'Unknown error'}`);
      }
    } else if (error.request) {
      // Network error
      console.error('❌ Network Error:', {
        message: 'No response received from server',
        url: error.config?.url,
        error: error.message
      });
    } else {
      // Something else happened
      console.error('❌ Request Setup Error:', error.message);
    }

    return Promise.reject(error);
  }
);

// Helper function to check if error is a network error
export const isNetworkError = (error) => {
  return !error.response && error.request;
};

// Helper function to check if error is a server error (5xx)
export const isServerError = (error) => {
  return error.response && error.response.status >= 500;
};

// Helper function to check if error is a client error (4xx)
export const isClientError = (error) => {
  return error.response && error.response.status >= 400 && error.response.status < 500;
};

// Helper function to get error message
export const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

// Helper function to get validation errors
export const getValidationErrors = (error) => {
  if (error.response?.data?.errors) {
    return error.response.data.errors;
  }
  if (error.response?.data?.details) {
    return error.response.data.details;
  }
  return [];
};

export default axiosInstance;
